import React, { useContext, useState, useEffect, useMemo } from 'react';
import { CreditCardField } from './CreditCardField';
import { EditableFieldsContext, EditableFieldsView } from '../context';
import { AchManagementSection } from 'features/account-settings/components/SettingsTab/PaymentOptions/ach/AddAchSection';
import { usePaymentOptions } from 'common/hooks/usePaymentOptions';
import Skeleton from 'react-loading-skeleton';
import { useRootSelector } from 'rootStore';
import { PaymentType } from 'common/api/e-comm/payment-types';
import { FieldDisplay } from '../PersonalSettings/utils';
import { Accordion } from 'features/product/components/Accordion';
import { useFromContentful } from 'common/api/contentful/utils';
import { selectIsCardLinkedOffersWebEnabled } from 'common/features/store/duck/ffs';

type ContentfulFaqItem = {
    title: string;
    content: string;
    faqType: string;
};

const PAYMENT_FAQ_TYPE = 'payment-settings';

export const PaymentOptionsFields = () => {
    const cardLinkedOffersEnabled = useRootSelector(selectIsCardLinkedOffersWebEnabled);
    const { fieldToEdit } = useContext(EditableFieldsContext);
    return (
        <div className="mb-8">
            <CreditCardField isEditing={fieldToEdit === 'creditcardfield'} cardLinkedOffers={cardLinkedOffersEnabled} />
        </div>
    );
};

export const PaymentSettingsContent = () => {
    const { loadPaymentOptionsData: loadPaymentOptions } = usePaymentOptions();
    const [dataLoading, setDataLoadingTo] = useState<boolean>(true);
    const { paymentTypes } = useRootSelector((s) => s.store.organization);
    const paymentSettingsFaq = useFromContentful<ContentfulFaqItem>('faqItem');

    const faqAccordionItems = useMemo(() => {
        const filteredFaqs =
            paymentSettingsFaq?.content?.items.filter((value) => {
                return value.fields.faqType === PAYMENT_FAQ_TYPE;
            }) || [];

        const items = filteredFaqs.map((item) => ({
            value: item.fields.title,
            title: <span className="font-semibold text-xs leading-1">{item.fields.title}</span>,
            content: item.fields.content ? (
                <div className="px-3 text-grey-1 text-sm">
                    <span>{item.fields.content}</span>
                </div>
            ) : (
                ''
            ),
        }));
        return items;
    }, [paymentSettingsFaq]);

    const achPaymentAllowed = (paymentTypes || []).some(
        (pt) => pt === PaymentType.BankAccount,
        paymentTypes
    );
    const creditCardPaymentAllowed = (paymentTypes || []).some(
        (pt) => pt === PaymentType.CreditCard,
        paymentTypes
    );
    const checkPaymentAllowed = (paymentTypes || []).some(
        (pt) => pt === PaymentType.Check,
        paymentTypes
    );

    useEffect(() => {
        (async () => {
            await loadPaymentOptions();
            setDataLoadingTo(false);
        })();
    }, [loadPaymentOptions]);

    if (dataLoading) {
        return <Skeleton className="h-40" />;
    }
    return (
        <>
            <EditableFieldsView>
                {achPaymentAllowed && <AchManagementSection />}

                {creditCardPaymentAllowed && <PaymentOptionsFields />}

                {checkPaymentAllowed && <CheckFieldDisplay />}
            </EditableFieldsView>

            {faqAccordionItems?.length > 0 && (
                <div className="account_payment_options_FAQ">
                    <span className="text-xl font-semibold text-brand-dark block mb-6">
                        Frequently Asked Questions
                    </span>
                    <Accordion items={faqAccordionItems} />
                </div>
            )}
        </>
    );
};

export const CheckFieldDisplay = () => (
    <div className="payment_options_pay_coordinator_section mb-8">
        <FieldDisplay
            id={'check_msg'}
            label="Pay Coordinator Directly"
            isEditing={false}
            renderers={{
                ToggleEditViewRenderer: () => null,
            }}
        >
            Pay your coordinator with cash, check, or a money transfer app.
        </FieldDisplay>
    </div>
);
